import type { QueryKey } from '@tanstack/react-query';

import type { CreditsafeRecordSearchQuery } from './clients/useCreditsafeRecord';
import type { CommunityProfileFilters } from './community/types';
import type { CreditNoteQueryMetadata } from './creditNotes';
import type { ForumTag } from './forum/types';
import type { InvoiceQueryMetadata } from './invoices';
import type { InvoiceTemplateQueryMetadata } from './invoiceTemplates';
import type { BookmarkedJobPostsQueryMetadata, JobPostsQueryMetadata, UserJobPostsQueryMetadata } from './jobPosts/types';
import type { QuotationQueryMetadata } from './quotations';

import { isDefaultCreditNoteQueryMetadata } from './creditNotes/useCreditNotes';
import { isDefaultInvoiceQueryMetadata } from './invoices/useInvoices';
import { isDefaultInvoiceTemplateQueryMetadata } from './invoiceTemplates/useInvoiceTemplates';
import { isDefaultQuotationQueryMetadata } from './quotations/useQuotations';

type QueryKeyDefinition = QueryKey | ((...args: any[]) => QueryKey);
type QueryKeysFactory = { [K: string]: QueryKeyDefinition | QueryKeysFactory };

export const QueryKeys = {
  user: ['user'] as const,

  contract: {
    root: ['contract'] as const,
    pdf: ['contract', 'pdf'] as const,
    addendumPdf: ['contract', 'addendumPdf'] as const,
  },

  quotations: {
    root: ['quotations'] as const,
    index: {
      root: ['quotations', 'index'] as const,
      filters: ['quotations', 'index', 'filters'] as const,
      metadata: (metadata: QuotationQueryMetadata) =>
        ['quotations', 'index', 'metadata', isDefaultQuotationQueryMetadata(metadata) ? 'default' : metadata] as const,
    },
    show: {
      root: ['quotations', 'show'] as const,
      id: (id: number) => ['quotations', 'show', id] as const,
      calculation: (id: number, dependencies: unknown) => ['quotations', 'show', id, 'calculation', JSON.stringify(dependencies)] as const,
      attachment: (id: number) => ['quotations', 'show', id, 'attachment'] as const,
    },
  },

  invoices: {
    root: ['invoices'] as const,
    index: {
      root: ['invoices', 'index'] as const,
      filters: ['invoices', 'index', 'filters'] as const,
      metadata: (metadata: InvoiceQueryMetadata) =>
        ['invoices', 'index', 'metadata', isDefaultInvoiceQueryMetadata(metadata) ? 'default' : metadata] as const,
    },
    show: {
      root: ['invoices', 'show'] as const,
      id: (id: number) => ['invoices', 'show', id] as const,
      calculation: (id: number, dependencies: unknown) => ['invoices', 'show', id, 'calculation', JSON.stringify(dependencies)] as const,
      attachment: (id: number) => ['invoices', 'show', id, 'attachment'] as const,
      history: (id: number) => ['invoices', 'show', id, 'history'] as const,
    },
    creditable: ['invoices', 'creditable'] as const,
  },

  creditNotes: {
    root: ['creditNotes'] as const,
    index: {
      root: ['creditNotes', 'index'] as const,
      filters: ['creditNotes', 'index', 'filters'] as const,
      metadata: (metadata: CreditNoteQueryMetadata) =>
        ['creditNotes', 'index', 'metadata', isDefaultCreditNoteQueryMetadata(metadata) ? 'default' : metadata] as const,
    },
  },

  invoiceTemplates: {
    root: ['invoiceTemplates'] as const,
    index: {
      root: ['invoiceTemplates', 'index'] as const,
      filters: ['invoiceTemplates', 'index', 'filters'] as const,
      metadata: (metadata: InvoiceTemplateQueryMetadata) =>
        ['invoiceTemplates', 'index', 'metadata', isDefaultInvoiceTemplateQueryMetadata(metadata) ? 'default' : metadata] as const,
    },
    show: {
      root: ['invoiceTemplates', 'show'] as const,
      id: (id: number) => ['invoiceTemplates', 'show', id] as const,
      calculation: (id: number, dependencies: unknown) =>
        ['invoiceTemplates', 'show', id, 'calculation', JSON.stringify(dependencies)] as const,
    },
  },

  products: {
    root: ['products'] as const,
    index: ['products', 'index'] as const,
  },

  clients: {
    root: ['clients'] as const,
    index: ['clients', 'index'] as const,
    peppolId: (vatNumber: string) => ['clients', vatNumber, 'peppolId'] as const,
    creditsafe: {
      root: ['clients', 'creditsafe'] as const,
      search: (searchQuery: CreditsafeRecordSearchQuery | null) => ['clients', 'creditsafe', searchQuery] as const,
    },
  },

  forum: {
    root: ['forum'] as const,
    index: {
      root: ['forum', 'posts', 'index'] as const,
      tag: (tag: ForumTag | null) => ['forum', 'posts', 'index', 'tag', tag] as const,
      user: (userId: number) => ['forum', 'posts', 'index', 'user', userId] as const,
      saved: ['forum', 'posts', 'index', 'saved'] as const,
    },
    show: {
      root: ['forum', 'posts', 'show'] as const,
      id: (id: number) => ['forum', 'posts', 'show', id] as const,
      comments: (id: number) => ['forum', 'posts', 'show', id, 'comments'] as const,
      systemMessages: (id: number) => ['forum', 'posts', 'show', id, 'systemMessages'] as const,
    },
  },

  community: {
    root: ['community'] as const,
    index: {
      root: ['community', 'index'] as const,
      metadata: (filters: CommunityProfileFilters) => ['community', 'index', filters] as const,
    },
    count: {
      root: ['community', 'count'] as const,
      metadata: (filters: CommunityProfileFilters) => ['community', 'count', filters] as const,
    },
    show: {
      root: ['community', 'show'] as const,
      id: (id: number) => ['community', 'show', id] as const,
    },
  },

  jobPosts: {
    root: ['jobPosts'] as const,
    index: {
      root: ['jobPosts', 'index'] as const,
      metadata: (metadata: JobPostsQueryMetadata) => ['jobPosts', 'index', 'metadata', metadata] as const,
      bookmarked: {
        root: ['jobPosts', 'index', 'bookmarked'] as const,
        metadata: (metadata: BookmarkedJobPostsQueryMetadata) => ['jobPosts', 'index', 'bookmarked', metadata] as const,
      },
      user: {
        root: ['jobPosts', 'index', 'user'] as const,
        metadata: (metadata: UserJobPostsQueryMetadata) => ['jobPosts', 'index', 'user', metadata] as const,
      },
    },
    show: {
      root: ['jobPosts', 'show'] as const,
      id: (id: number) => ['jobPosts', 'show', id] as const,
    },
  },

  conditions: {
    root: ['conditions'] as const,
    index: ['conditions', 'index'] as const,
    pdf: (id: number) => ['conditions', id, 'pdf'] as const,
  },

  customConditions: {
    root: ['customConditions'] as const,
    index: ['customConditions', 'index'] as const,
  },

  statistics: {
    root: ['statistics'] as const,
    dashboard: ['statistics', 'dashboard'] as const,
    invitations: ['statistics', 'invitations'] as const,
    pricing: ['statistics', 'pricing'] as const,
  },

  misc: {
    heartbeat: ['heartbeat'] as const,
    quotationForClient: (approvalKey: string) => ['quotationForClient', approvalKey] as const,
  },
} satisfies QueryKeysFactory;
